import { useNavigate, useSearchParams } from 'react-router';
import { useCurrentUser } from '../api/session';
import { type Metrics, PERIODS, type PeriodDays, percent, type TaskStats, useMetrics } from '../api/tasks';
import { Avatar } from '../components/Avatar';
import { downloadCsv } from '../components/csv';
import { Icon } from '../components/Icon';
import { formatNumber, KpiCard, PeriodPills } from '../components/Stats';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import { usePageTitle } from '../layout/shellContext';

function StatsCards({ stats }: { stats: TaskStats }) {
  return (
    <div className="kpi-grid">
      <KpiCard label="Всего задач" icon="list" value={formatNumber(stats.total)} />
      <KpiCard
        label="Завершено"
        icon="check"
        value={`${percent(stats.completed, stats.total)} %`}
        extra={formatNumber(stats.completed)}
        tone="good"
      />
      <KpiCard label="Не завершено" icon="clock" value={formatNumber(stats.open)} />
      <KpiCard
        label="Просрочено"
        icon="alert"
        value={formatNumber(stats.overdue)}
        tone={stats.overdue ? 'bad' : undefined}
      />
    </div>
  );
}

function Cells({ stats }: { stats: TaskStats }) {
  const done = percent(stats.completed, stats.total);
  return (
    <>
      <td>
        <div className="progress">
          <div className="track">
            <i style={{ width: `${done}%` }} />
          </div>
          <span className="number">{done} %</span>
        </div>
      </td>
      <td className="number">{formatNumber(stats.open)}</td>
      <td className={`number${stats.overdue ? ' bad' : ''}`}>{formatNumber(stats.overdue)}</td>
    </>
  );
}

const periodLabel = (days: PeriodDays) => PERIODS.find((p) => p.days === days)?.label ?? `${days} дней`;

function exportMetrics(data: Metrics) {
  const row = (name: string, s: TaskStats) => [
    name,
    s.total,
    s.completed,
    percent(s.completed, s.total),
    s.open,
    s.overdue,
  ];
  downloadCsv(
    `Показатели — ${data.department?.name ?? 'мои'} — ${periodLabel(data.period)}.csv`,
    ['Кто', 'Всего', 'Завершено', 'Завершено, %', 'Не завершено', 'Просрочено'],
    [...data.units.map((u) => row(u.name, u)), ...data.people.map((p) => row(p.fullName, p))],
  );
}

/** «Показатели»: задачи Bitrix24 по подразделениям и сотрудникам, которых видит пользователь. */
export function MetricsPage() {
  usePageTitle('Показатели');
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const periodParam = Number(params.get('period'));
  const period = (PERIODS.find((p) => p.days === periodParam)?.days ?? 30) as PeriodDays;
  const departmentId = params.get('department');
  const metrics = useMetrics(period, departmentId);

  const update = (patch: Record<string, string | null>) =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(patch)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    });

  if (metrics.isPending) return <LoadingState label="Считаю показатели" />;
  if (metrics.isError) return <ErrorState error={metrics.error} onRetry={() => void metrics.refetch()} />;
  const data = metrics.data;

  if (!data.totals) {
    return (
      <section className="panel">
        <EmptyState title="Показатели вам не видны">Они доступны сотрудникам, руководителям и HR.</EmptyState>
      </section>
    );
  }

  const openCabinet = (id: string) =>
    navigate(id === me.data?.employeeId ? '/me#tasks' : `/employees/${id}#tasks`);

  return (
    <div className="page">
      <section className="panel period-bar">
        <div className="filters">
          {data.scopes.length > 1 && data.department ? (
            <select
              className="select"
              aria-label="Подразделение"
              value={data.department.id}
              onChange={(event) => update({ department: event.target.value })}
            >
              {data.scopes.map((scope) => (
                <option key={scope.id} value={scope.id}>
                  {'· '.repeat(scope.depth)}
                  {scope.name} ({scope.people})
                </option>
              ))}
            </select>
          ) : null}
        </div>
        <PeriodPills value={period} onChange={(days) => update({ period: String(days) })} />
      </section>

      {!data.sync.ready ? (
        <p className="notice">
          <Icon name="clock" size={14} />
          Задачи ещё загружаются из Bitrix24: загружено {formatNumber(data.sync.loaded)}. Числа пока неполные.
        </p>
      ) : null}

      {data.department && data.department.path.length > 1 ? (
        <nav className="org-path" aria-label="Путь">
          {data.department.path.map((step, index) => (
            <span key={step.id} style={{ display: 'contents' }}>
              {index ? (
                <span className="sep">
                  <Icon name="chevronRight" size={12} />
                </span>
              ) : null}
              <button
                type="button"
                aria-current={step.id === data.department?.id ? 'page' : undefined}
                onClick={() => update({ department: step.id })}
              >
                {step.name}
              </button>
            </span>
          ))}
        </nav>
      ) : null}

      <StatsCards stats={data.totals} />

      {data.department && (data.units.length || data.people.length) ? (
        <section className="panel">
          <div className="panel-header">
            <h2>{data.department.name}</h2>
            <button type="button" className="btn-secondary" onClick={() => exportMetrics(data)}>
              <Icon name="download" size={14} />
              Выгрузить
            </button>
          </div>
          <div className="table-scroll">
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Кто</th>
                  <th>Завершено</th>
                  <th className="number">Не завершено</th>
                  <th className="number">Просрочено</th>
                </tr>
              </thead>
              <tbody>
                {data.units.map((unit) => (
                  <tr key={unit.id} className="clickable" onClick={() => update({ department: unit.id })}>
                    <td>
                      <button
                        type="button"
                        className="row-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          update({ department: unit.id });
                        }}
                      >
                        <strong>{unit.name}</strong>
                      </button>
                      <small>{formatNumber(unit.people)} чел.</small>
                    </td>
                    <Cells stats={unit} />
                  </tr>
                ))}
                {data.people.map((person) => (
                  <tr key={person.id} className="clickable" onClick={() => openCabinet(person.id)}>
                    <td>
                      <button
                        type="button"
                        className="person-line row-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          openCabinet(person.id);
                        }}
                      >
                        <Avatar name={person.fullName} photoUrl={person.photoUrl} />
                        <span className="person-line-text">
                          <b>{person.fullName}</b>
                          {person.position ? <small>{person.position}</small> : null}
                        </span>
                      </button>
                    </td>
                    <Cells stats={person} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
